import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const ListarEnteConformador = () => {
  const [entes, setEntes] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🗑️ Eliminar
  const deleteEnte = useCallback(async (id) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡No podrá revertir este proceso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        const { data } = await axios.delete(`/api/ente/${id}`);

        Swal.fire({
          icon: "success",
          title: data.message,
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error("Error al eliminar:", error.message);
      Swal.fire("Error", "No se pudo eliminar.", "error");
    }
  }, []);

  // 📥 Obtener datos
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/ente/listall/");
      const lista = data?.entesConformadores || [];

      const entesConFunciones = lista
        .map((ente) => ({
          ...ente,

          // 🔢 Dane alineado derecha
          coddaneEnteconformador: (
            <div className="text-end w-100">
              {ente.coddaneEnteconformador}
            </div>
          ),

          // 🔢 Nit alineado derecha
          nitEnteconformador: (
            <div className="text-end w-100">
              {ente.nitEnteconformador}
            </div>
          ),

          // 🏷️ Nombre alineado derecha + truncate
          nombreEnteconformador: (
            <div className="text-end w-100">
              <div title={ente.nombreEnteconformador} >
                {ente.nombreEnteconformador}
              </div>
            </div>
          ),

          // 📞 Teléfono alineado derecha
          telefono1Enteconformador: (
            <div className="text-end w-100">
              {ente.telefono1Enteconformador}
            </div>
          ),

          // 🗺️ Estado alineado derecha + truncate
          nombreEstado: (
            <div className="text-end w-100">
              <div title={ente.estadoObjectId?.nombreEstado || ""} >
                {ente.estadoObjectId?.nombreEstado || "Estado no disponible"}
              </div>
            </div>
          ),

          // 🌆 Ciudad alineado derecha + truncate
          nombreCiudad: (
            <div className="text-end">
              <div
                className="text-truncate"
                style={{ maxWidth: 320 }}
                title={ente.ciudadObjectId?.nombreCiudad || ""}
              >
                {ente.ciudadObjectId?.nombreCiudad || "Ciudad no disponible"}
              </div>
            </div>
          ),

          // ⚙️ Funciones
          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/enteconformador/asociar/jefeinmediato/${ente._id}`)
                }
                title="Asociar Rector"
                type="button"
              >
                <i className="fa-solid fa-link"></i>
              </button>

              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/enteconformador/resoluciones/${ente._id}`)
                }
                title="Listar Resoluciones"
                type="button"
              >
                <i className="fa-solid fa-list"></i>
              </button>

              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/enteconformador/ente/editar/${ente._id}`)
                }
                title="Editar Ente"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>

              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={async () => {
                  await deleteEnte(ente._id);
                  getData();
                }}
                title="Eliminar Ente"
                type="button"
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        }))
        .reverse();

      setEntes(entesConFunciones);
    } catch (error) {
      console.error("Error al cargar:", error.message);
      setEntes([]);
    } finally {
      setLoading(false);
    }
  }, [deleteEnte, navigate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const columns = useMemo(
    () => [
      { Header: "Dane", accessor: "coddaneEnteconformador" },
      { Header: "Nit", accessor: "nitEnteconformador" },
      { Header: "Nombre", accessor: "nombreEnteconformador" },
      { Header: "Teléfono 1", accessor: "telefono1Enteconformador" },
      { Header: "Estado", accessor: "nombreEstado" },
      { Header: "Ciudad", accessor: "nombreCiudad" },
      { accessor: "funciones" },
    ],
    []
  );

  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

        <div className="px-3 px-md-4 py-3 border-bottom bg-white">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h5 className="mt-2 mb-1 fw-semibold">
                Entes Conformadores
              </h5>
              <small className="text-secondary">
                Gestión institucional y resoluciones.
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-outline-secondary"
                onClick={getData}
                disabled={loading}
              >
                <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>
                Actualizar
              </button>

              <button
                className="btn btn-dark"
                onClick={() => navigate("/enteconformador/ente/nuevo/")}
              >
                <i className="fa-solid fa-plus me-2"></i>
                Nuevo
              </button>
            </div>
          </div>
        </div>

        <div>
          {loading ? (
            <div className="text-center text-secondary py-5">
              <div className="spinner-border" />
              <div className="mt-3">Cargando entes…</div>
            </div>
          ) : entes.length > 0 ? (
            <div className="overflow-hidden">
              <DataTable columns={columns} data={entes} />
            </div>
          ) : (
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-2">🏛️</div>
              <div className="fw-semibold">No hay entes registrados</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListarEnteConformador;